import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../axios";
import { getDayType, DATE_BADGE } from "../utils/dayType";
import { useDayTypeVersion } from "./DayTypeProvider";
import { canUseAnalysis } from "../utils/analysisAccess";
import { useRegularDay, RegularBand, RegularTag, RegularDutyModal } from "./RegularDuty";
// 編集フォームは過去データ（ArchiveModal）と共通
import SightingEditForm, { IconPencil, inputBase, selectBase } from "./SightingEditForm";
import { useCommentSuggestions, CommentAssistButton, CommentAssistModal } from "./CommentAssist";

const IconX = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const IconTrash = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4h6v2" />
  </svg>
);
const IconFlag = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
    <line x1="4" y1="22" x2="4" y2="15" />
  </svg>
);
const IconUser = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
  </svg>
);
const IconClock = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
  </svg>
);
const IconChat = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);
const IconAlert = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

function SightingModal({ isOpen, onClose, onPosted, depot, bus_number, date, sightings = [], currentUser }) {
  // 特別日が API から届いたら描き直す（日付の色分けが getDayType に依存している）
  useDayTypeVersion();

  const navigate = useNavigate();

  const [routes, setRoutes] = useState([]);
  const [starts, setStarts] = useState([]);
  const [routeId, setRouteId] = useState("");
  const [startId, setStartId] = useState("");
  const [time, setTime] = useState(() => new Date().toTimeString().slice(0, 5));
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("error");

  const [reportingId, setReportingId] = useState(null);
  const [reportReason, setReportReason] = useState("");

  const [editingId, setEditingId] = useState(null);

  // コメント候補。曜日の既定は投稿日のダイヤ種別で、モーダルで選び直せる（null = 自動）
  const [assistOpen, setAssistOpen] = useState(false);
  const [dayTypeOverride, setDayTypeOverride] = useState(null);
  const autoDayType = getDayType(date);
  const assistDayType = dayTypeOverride || autoDayType;
  const suggestions = useCommentSuggestions({
    enabled: isOpen && !!currentUser,
    routeId,
    startId,
    time,
    dayType: assistDayType,
    date,
  });
  const assistReady = !!(routeId && startId && time);

  const isSuperAdmin = currentUser?.user_role_id === 1;

  // 所定（帯・カードの札・便の一覧）。**マイページの印のオン／オフは見ない**
  const analysisOn = canUseAnalysis(currentUser);
  const [dutyOpen, setDutyOpen] = useState(false);
  const regularDay = useRegularDay({
    enabled: isOpen && analysisOn,
    depot,
    busNumber: bus_number,
    date,
    reloadKey: sightings,
  });
  const regularMarks = regularDay?.marks || {};

  useEffect(() => {
    if (isOpen) {
      setMessage("");
      setEditingId(null);
      setReportingId(null);
      setReportReason("");
      setDutyOpen(false);
      setAssistOpen(false);
      setDayTypeOverride(null);
    }
  }, [isOpen, date]);

  useEffect(() => {
    const fetchRoutes = async () => {
      try {
        if (depot) {
          const res = await axios.get(`/routes?depot=${encodeURIComponent(depot)}`);
          setRoutes(res.data);
        } else setRoutes([]);
      } catch (err) {
        console.error("路線取得失敗:", err);
      }
    };
    fetchRoutes();
  }, [depot]);

  useEffect(() => {
    const fetchStarts = async () => {
      try {
        if (routeId) {
          const res = await axios.get(`/starts?route_id=${routeId}`);
          setStarts(res.data);
        } else setStarts([]);
      } catch (err) {
        console.error("始発地取得失敗:", err);
      }
    };
    fetchStarts();
  }, [routeId]);

  const showMsg = (text, type = "error") => { setMessage(text); setMessageType(type); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!routeId || !startId || !time) {
      showMsg("路線・始発地・時刻は必須です。", "warning");
      return;
    }
    try {
      setLoading(true);
      await axios.post("/sightings", {
        bus_number,
        route_id: parseInt(routeId),
        start_id: parseInt(startId),
        started_at: `${date} ${time}:00`,
        comment,
        user_id: currentUser?.id ?? null,
      });
      setComment("");
      setRouteId("");
      setStartId("");
      if (onPosted) onPosted("投稿が完了しました");
    } catch (err) {
      showMsg(err.response?.data?.message || "投稿に失敗しました。");
    } finally {
      setLoading(false);
    }
  };

  const handleEditOpen = (s) => setEditingId(s.postId);

  const handleEditCancel = () => setEditingId(null);

  const handleEditSaved = (text) => {
    setEditingId(null);
    if (onPosted) onPosted(text);
  };

  const handleDelete = async (postId) => {
    if (!window.confirm("この投稿を削除しますか？")) return;
    try {
      const endpoint = isSuperAdmin ? `/admin/posts/${postId}` : `/sightings/${postId}`;
      await axios.delete(endpoint);
      if (onPosted) onPosted("投稿を削除しました");
    } catch {
      showMsg("削除に失敗しました。");
    }
  };

  const openReportModal = (postId) => { setReportingId(postId); setReportReason(""); };

  const submitReport = async () => {
    if (!reportingId) return;
    const url = currentUser
      ? `/sightings/${reportingId}/report`
      : `/sightings/${reportingId}/report-guest`;
    try {
      await axios.post(url, { reason: reportReason });
      if (onPosted) onPosted("通報を受け付けました");
    } catch {
      showMsg("通報に失敗しました。");
    } finally {
      setReportingId(null);
      setReportReason("");
    }
  };

  if (!isOpen) return null;

  const getColor = (name) => {
    if (!routes.length) return "#64748b";
    const match = routes.find((r) => r.full_name === name || r.short_name === name);
    return match ? match.color : "#64748b";
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    return `${year.slice(-2)}/${month}/${day}`;
  };

  const sortedSightings = [...sightings].sort((a, b) => (a.time || "").localeCompare(b.time || ""));

  return (
    <>
      {/* オーバーレイ */}
      <div
        style={{
          position: "fixed", inset: 0,
          backgroundColor: "rgba(2,12,8,0.65)",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          zIndex: 1000,
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: "16px", boxSizing: "border-box",
        }}
        onClick={onClose}
      >
        {/* モーダル本体 */}
        <div
          style={{
            background: "#fff",
            borderRadius: "24px",
            width: "100%",
            maxWidth: "480px",
            maxHeight: "90dvh",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 32px 80px rgba(0,0,0,0.35), 0 4px 16px rgba(0,0,0,0.12)",
            overflow: "hidden",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* ドラッグバー＋ヘッダー */}
          <div style={{
            background: "linear-gradient(170deg, #d1fae5 0%, #a7f3d0 100%)",
            borderBottom: "1px solid #6ee7b7",
            flexShrink: 0,
          }}>
            <div style={{ padding: "10px 0 0", display: "flex", justifyContent: "center" }}>
              <div style={{ width: 40, height: 4, borderRadius: 99, background: "#047857" }} />
            </div>
          <div style={{
            padding: "10px 16px 11px",
            display: "flex", justifyContent: "space-between", alignItems: "center",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
              <span style={{
                fontSize: "22px", fontWeight: "800", color: "#064e3b",
                letterSpacing: "-0.5px", lineHeight: 1,
              }}>
                {depot}{bus_number}
              </span>
              <span style={{
                fontSize: "12px", fontWeight: "700",
                background: DATE_BADGE[getDayType(date)].bg,
                border: `1px solid ${DATE_BADGE[getDayType(date)].border}`,
                color: DATE_BADGE[getDayType(date)].color,
                borderRadius: "20px", padding: "3px 10px",
                letterSpacing: "0.3px",
                flexShrink: 0,
              }}>
                {formatDisplayDate(date)}
              </span>
            </div>
            <button
              onClick={onClose}
              style={{
                background: "#fff1f2",
                border: "1px solid #fecdd3",
                borderRadius: "50%",
                width: 36, height: 36,
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", color: "#be123c", flexShrink: 0,
                WebkitTapHighlightColor: "transparent",
              }}
            >
              <IconX size={17} />
            </button>
          </div>
          </div>

          {/* 所定の帯。タップで所定の便の一覧（運用解析への導線もそこにある）。
              公開範囲は utils/analysisAccess.js で切り替える */}
          {analysisOn && (
            <div style={{ flexShrink: 0 }}>
              <RegularBand day={regularDay} onOpen={() => setDutyOpen(true)} />
            </div>
          )}

          {/* スクロールエリア */}
          <div style={{ overflowY: "auto", overflowX: "hidden", WebkitOverflowScrolling: "touch", flex: 1 }}>

            {/* 投稿フォーム */}
            {currentUser ? (
              <form onSubmit={handleSubmit} style={{
                padding: "12px 14px",
                background: "#f8fffe",
                borderBottom: "1px solid #d1fae5",
              }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "7px" }}>
                  <select
                    className="sm-input"
                    value={routeId}
                    onChange={(e) => setRouteId(e.target.value)}
                    style={selectBase}
                  >
                    <option value="">路線を選択</option>
                    {routes.map((r) => (
                      <option key={r.id} value={r.id}>[{r.short_name}] {r.full_name}</option>
                    ))}
                  </select>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <select
                      className="sm-input"
                      value={startId}
                      onChange={(e) => setStartId(e.target.value)}
                      disabled={!routeId}
                      style={{ ...selectBase, flex: "1 1 0", minWidth: 0, width: "auto", opacity: routeId ? 1 : 0.45 }}
                    >
                      <option value="">始発バス停を選択</option>
                      {starts.map((s) => (
                        <option key={s.id} value={s.id}>[{s.short_name}] {s.full_name}</option>
                      ))}
                    </select>
                    <input
                      className="sm-input"
                      type="time"
                      value={time}
                      onChange={(e) => setTime(e.target.value)}
                      style={{ ...inputBase, width: "auto", flexShrink: 0 }}
                    />
                  </div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <input
                      className="sm-input"
                      type="text"
                      placeholder="コメント（行先番号など・任意・100文字以内）"
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      maxLength={100}
                      style={{ ...inputBase, flex: "1 1 0", minWidth: 0, width: "auto" }}
                    />
                    <CommentAssistButton
                      ready={assistReady}
                      lit={assistReady && !suggestions.loading && suggestions.items.length > 0}
                      count={suggestions.items.length}
                      onClick={() => setAssistOpen(true)}
                    />
                  </div>
                  {message && (
                    <div style={{
                      display: "flex", alignItems: "center", gap: "6px",
                      fontSize: "13px", padding: "9px 11px", borderRadius: "10px",
                      background: messageType === "warning" ? "#fffbeb" : "#fef2f2",
                      color: messageType === "warning" ? "#b45309" : "#b91c1c",
                      border: `1px solid ${messageType === "warning" ? "#fde68a" : "#fca5a5"}`,
                    }}>
                      <span style={{ flexShrink: 0 }}><IconAlert size={14} /></span>
                      {message}
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      padding: "10px", borderRadius: "10px", border: "none",
                      background: loading
                        ? "#a7f3d0"
                        : "linear-gradient(135deg, #059669 0%, #10b981 100%)",
                      color: "#fff", fontWeight: "700", fontSize: "15px",
                      cursor: loading ? "not-allowed" : "pointer",
                      letterSpacing: "0.2px",
                      boxShadow: loading ? "none" : "0 4px 14px rgba(16,185,129,0.4)",
                      WebkitTapHighlightColor: "transparent",
                    }}
                  >
                    {loading ? "投稿中..." : "投稿する"}
                  </button>
                </div>
              </form>
            ) : (
              <div style={{
                padding: "12px 14px",
                borderBottom: "1px solid #d1fae5",
                background: "#f8fffe",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <div style={{
                  display: "flex", alignItems: "center", gap: "8px",
                  background: "#fff1f2",
                  border: "1px solid #fecdd3",
                  color: "#be123c",
                  fontSize: "14px", fontWeight: "700",
                  padding: "10px 20px", borderRadius: "12px",
                  letterSpacing: "0.2px",
                }}>
                  <IconAlert size={15} />
                  ログインすると目撃情報を投稿できます
                </div>
              </div>
            )}

            {/* 目撃履歴 */}
            <div style={{ padding: "16px" }}>
              <div style={{
                fontSize: "11px", fontWeight: "700", color: "#10b981",
                letterSpacing: "1.2px", textTransform: "uppercase",
                marginBottom: "12px",
                display: "flex", alignItems: "center", gap: "8px",
              }}>
                <span style={{
                  display: "inline-block", width: 3, height: 14,
                  background: "linear-gradient(180deg,#059669,#34d399)",
                  borderRadius: 2,
                }} />
                目撃履歴 {sortedSightings.length}件
              </div>

              {sortedSightings.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  {sortedSightings.map((s) => {
                    const isOwner = currentUser?.id === s.userId;
                    const canEditDelete = isSuperAdmin || isOwner;
                    const isEditing = editingId === s.postId;

                    return (
                      <div
                        key={s.postId}
                        style={{
                          borderRadius: "14px",
                          border: "1px solid #e8f0eb",
                          background: "#fff",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.04)",
                          overflow: "hidden",
                          minWidth: 0,
                        }}
                      >
                        {isEditing ? (
                          /* 編集フォーム（過去データと共通） */
                          <SightingEditForm
                            record={s}
                            date={date}
                            busNumber={bus_number}
                            routes={routes}
                            isSuperAdmin={isSuperAdmin}
                            onSaved={handleEditSaved}
                            onCancel={handleEditCancel}
                            onError={showMsg}
                          />
                        ) : (
                          /* 表示カード */
                          <div style={{ padding: "13px 13px 11px" }}>
                            {/* 路線バッジ */}
                            <div style={{
                              display: "inline-block",
                              maxWidth: "100%",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              verticalAlign: "top",
                              background: getColor(s.route_full_name || s.route),
                              color: "#fff", borderRadius: "20px",
                              padding: "3px 10px", fontSize: "11px", fontWeight: "800",
                              marginBottom: "8px",
                              letterSpacing: "0.2px",
                            }}>
                              {s.route_full_name || s.route}
                            </div>
                            {/* 所定の便なら札（その日の朝の所定） */}
                            <RegularTag duty={regularMarks[s.postId]} />
                            {/* 始発 + 時刻 */}
                            <div style={{
                              display: "flex",
                              alignItems: "baseline",
                              overflow: "hidden",
                              marginBottom: s.comment ? "8px" : "10px",
                              gap: "4px",
                            }}>
                              <div style={{
                                flex: "0 1 auto",
                                minWidth: 0,
                                fontSize: "16px", fontWeight: "700", color: "#0f172a",
                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                letterSpacing: "-0.2px",
                              }}>
                                {s.start_full_name || s.start}
                              </div>
                              <div style={{
                                flexShrink: 0,
                                color: "#059669", fontSize: "16px", fontWeight: "800",
                                whiteSpace: "nowrap",
                                fontVariantNumeric: "tabular-nums",
                                letterSpacing: "-0.5px",
                                lineHeight: 1,
                              }}>
                                {s.time}
                              </div>
                            </div>
                            {/* コメント */}
                            {s.comment && (
                              <div style={{
                                display: "flex", alignItems: "flex-start", gap: "8px",
                                fontSize: "13px", color: "#475569",
                                marginBottom: "10px",
                                borderLeft: "2.5px solid #34d399",
                                paddingLeft: "10px",
                              }}>
                                <span style={{ flexShrink: 0, color: "#10b981", marginTop: "2px" }}>
                                  <IconChat size={12} />
                                </span>
                                <span style={{ lineHeight: 1.5 }}>{s.comment}</span>
                              </div>
                            )}
                            {/* フッター: 左列=メタ情報 / 右列=ボタン */}
                            <div style={{
                              display: "flex", alignItems: "center",
                              justifyContent: "space-between", gap: "8px",
                              paddingTop: "8px",
                              borderTop: "1px solid #f1f5f4",
                            }}>
                              {/* 左: ユーザ名・投稿時刻を縦積み */}
                              <div style={{
                                display: "flex", flexDirection: "column", gap: "3px",
                                fontSize: "11.5px", color: "#94a3b8", minWidth: 0,
                              }}>
                                <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                  <IconUser size={11} />
                                  {s.userId ? (
                                    <a href={`/users/${s.userId}`} style={{ color: "#3b82f6", textDecoration: "none", fontWeight: "500" }}>
                                      {s.user || "名無し"}
                                    </a>
                                  ) : (
                                    <span>{s.user || "名無し"}</span>
                                  )}
                                </span>
                                <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                  <IconClock size={11} />{s.postedAt || "??/??"}
                                </span>
                              </div>
                              {/* 右: アクションボタン */}
                              <div style={{ display: "flex", gap: "6px", flexShrink: 0 }}>
                                {canEditDelete ? (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => handleEditOpen(s)}
                                      style={{
                                        display: "flex", alignItems: "center", gap: "4px",
                                        padding: "5px 11px", borderRadius: "8px",
                                        border: "1px solid #a7f3d0",
                                        background: "#ecfdf5", color: "#065f46",
                                        cursor: "pointer", fontSize: "12px", fontWeight: "600",
                                        WebkitTapHighlightColor: "transparent",
                                      }}
                                    >
                                      <IconPencil size={12} /> 編集
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleDelete(s.postId)}
                                      style={{
                                        display: "flex", alignItems: "center", gap: "4px",
                                        padding: "5px 11px", borderRadius: "8px",
                                        border: "1px solid #fca5a5",
                                        background: "#fef2f2", color: "#991b1b",
                                        cursor: "pointer", fontSize: "12px", fontWeight: "600",
                                        WebkitTapHighlightColor: "transparent",
                                      }}
                                    >
                                      <IconTrash size={12} /> 削除
                                    </button>
                                  </>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => openReportModal(s.postId)}
                                    style={{
                                      display: "flex", alignItems: "center", gap: "4px",
                                      padding: "5px 11px", borderRadius: "8px",
                                      border: "1px solid #fde68a",
                                      background: "#fffbeb", color: "#92400e",
                                      cursor: "pointer", fontSize: "12px", fontWeight: "600",
                                      WebkitTapHighlightColor: "transparent",
                                    }}
                                  >
                                    <IconFlag size={12} /> 通報
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ textAlign: "center", color: "#94a3b8", fontSize: "14px", padding: "32px 0" }}>
                  目撃情報がありません
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 通報サブモーダル */}
      {reportingId && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 1100,
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "24px", boxSizing: "border-box",
            backgroundColor: "rgba(2,12,8,0.55)",
            backdropFilter: "blur(4px)",
            WebkitBackdropFilter: "blur(4px)",
          }}
          onClick={() => setReportingId(null)}
        >
          <div
            style={{
              background: "#fff", borderRadius: "20px", padding: "22px",
              width: "100%", maxWidth: "340px",
              boxShadow: "0 20px 48px rgba(0,0,0,0.25), 0 4px 12px rgba(0,0,0,0.08)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 14px", fontSize: "16px", fontWeight: "700", color: "#0f172a" }}>
              通報理由
            </h3>
            <textarea
              className="sm-input"
              value={reportReason}
              onChange={(e) => setReportReason(e.target.value)}
              placeholder="理由（任意）"
              style={{
                ...inputBase,
                height: "80px", resize: "none", marginBottom: "14px",
              }}
            />
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                onClick={() => setReportingId(null)}
                style={{
                  flex: 1, padding: "11px", borderRadius: "10px",
                  border: "1.5px solid #e2e8f0", background: "#fff",
                  color: "#64748b", cursor: "pointer", fontSize: "14px", fontWeight: "600",
                  WebkitTapHighlightColor: "transparent",
                }}
              >
                キャンセル
              </button>
              <button
                onClick={submitReport}
                style={{
                  flex: 1, padding: "11px", borderRadius: "10px", border: "none",
                  background: "linear-gradient(135deg, #dc2626, #ef4444)",
                  color: "#fff", fontWeight: "700", fontSize: "14px",
                  cursor: "pointer",
                  boxShadow: "0 3px 10px rgba(220,38,38,0.35)",
                  WebkitTapHighlightColor: "transparent",
                }}
              >
                送信
              </button>
            </div>
          </div>
        </div>
      )}

      {/* コメント候補 */}
      <CommentAssistModal
        isOpen={assistOpen}
        onClose={() => setAssistOpen(false)}
        onPick={(c) => { setComment(c.slice(0, 100)); setAssistOpen(false); }}
        suggestions={suggestions}
        dayType={assistDayType}
        autoDayType={autoDayType}
        onDayTypeChange={(t) => setDayTypeOverride(t === autoDayType ? null : t)}
        routeLabel={routes.find((r) => String(r.id) === String(routeId))?.full_name || ""}
        startLabel={starts.find((st) => String(st.id) === String(startId))?.full_name || ""}
        time={time}
      />

      {/* 帯から開く所定の便の一覧。運用解析ページへの入口はここに置く */}
      <RegularDutyModal
        isOpen={dutyOpen}
        onClose={() => setDutyOpen(false)}
        day={regularDay}
        depot={depot}
        busNumber={bus_number}
        date={date}
        onGoAnalysis={() => navigate(`/analysis/buses/${encodeURIComponent(bus_number)}?date=${date}&depot=${encodeURIComponent(depot)}`)}
      />
    </>
  );
}

export default SightingModal;
